// Legacy VÉRO state (v1 … v910) → canonical VÉRO OS dataset.
//
// Principles
//  • Aliases are resolved exactly once, here (ports of core normalizeClient/normalizeContract/
//    normalizeTask/normalizeProspectItem + v600/v603/v900 shapes).
//  • Deterministic ids: stableId(kind, legacyId) → re-importing the same data is idempotent.
//  • Lossless: every imported row keeps its original object in `legacy`; collections without a
//    rebuilt module become LegacyRecords; unknown top-level keys are preserved too.
//  • Secrets are never imported (plaintext password, activation codes, login flags) — reported instead.
//  • Every decision that loses precision is reported as a warning with a reference.
import {
  emptyDataset, normalizeDateTime, normalizeDay, normalizeTime, stableId, validateDataset, splitName, searchKey, phoneMatchKey,
  PROSPECT_STATUSES, CASE_STAGES, REQUIREMENT_CATEGORIES,
  type Case, type CaseStage, type Dataset, type Person, type Policy, type PolicyStatus, type Priority, type ProspectStatus,
  type RequirementCategory, type ReviewOutcome, type TaskKind, type TaskStatus, type TimelineType, type PaymentFrequency, type ContactPoint,
} from '@vero/domain';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'object' ? '' : String(v).trim());

/** legacy firstNonEmpty(): 0 and false count as values; empty strings/arrays do not */
function first(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === 0 || v === false) return v;
    if (v === null || v === undefined) continue;
    if (Array.isArray(v)) { if (v.length) return v; continue; }
    if (typeof v === 'object') continue;
    if (String(v).trim() !== '') return v;
  }
  return '';
}
function firstArr(...vals: unknown[]): unknown[] { for (const v of vals) if (Array.isArray(v) && v.length) return v; return []; }
function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/\s/g, '').replace(/,(\d{1,2})$/, '.$1').replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}
const opt = (v: unknown): string | undefined => str(v) || undefined;
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface ImportWarning { code: string; message: string; ref?: string; count?: number }
export interface ImportReport {
  sourceVersion: string;
  importedAt: string;
  legacyCounts: Record<string, number>;
  produced: Record<string, number>;
  warnings: ImportWarning[];
  excludedSecrets: string[];
  preservedKinds: Record<string, number>;
  validationIssues: number;
}
export interface MapResult { dataset: Dataset; report: ImportReport }

export interface MapOptions { importedAt: string; externalNotes?: unknown[]; extraRecords?: { title: string; payload: unknown }[]; region?: string; sourceLabel?: string }

// --- value maps ---------------------------------------------------------------------
const N = (s: unknown) => searchKey(str(s));
function mapStage(status: unknown, hasActive: boolean): Person['stage'] {
  const s = N(status);
  if (/^(client|πελατης|customer|active client)$/.test(s)) return 'client';
  if (/(former|πρωην|ex client)/.test(s)) return 'former';
  if (/^(lead|new lead|νεα επαφη|επαφη)$/.test(s)) return hasActive ? 'client' : 'lead';
  if (/(not client|prospect|υποψηφ|μη πελατης)/.test(s)) return hasActive ? 'client' : 'prospect';
  return hasActive ? 'client' : 'prospect';
}
function mapPolicyStatus(v: unknown, warn: (m: string) => void): PolicyStatus {
  const s = N(v);
  if (!s) return 'offer';
  if (/^(active|ενεργο|in force|ισχυει)$/.test(s)) return 'active';
  if (/^(renewed|ανανεωθηκε|ανανεωση)$/.test(s)) return 'renewed';
  if (/^(offer|προσφορα|quote|quotation)$/.test(s)) return 'offer';
  if (/^(pending|εκκρεμ|σε εκκρεμοτητα|submitted|υποβληθηκε)/.test(s)) return 'pending';
  if (/^(cancelled|canceled|ακυρωθηκε|ακυρωση|ακυρωμενο)$/.test(s)) return 'cancelled';
  if (/^(lapsed|ανενεργο|suspended|paid up)$/.test(s)) return 'lapsed';
  if (/^(matured|expired|εληξε|ληξη)$/.test(s)) return 'matured';
  warn(String(v));
  return 'pending';
}
function mapFrequency(v: unknown): PaymentFrequency | undefined {
  const s = N(v);
  if (!s) return undefined;
  if (/(month|μην)/.test(s)) return 'monthly';
  if (/(quarter|τριμ)/.test(s)) return 'quarterly';
  if (/(semi|half|εξαμ)/.test(s)) return 'semiannual';
  if (/(single|εφαπαξ|once)/.test(s)) return 'single';
  if (/(annual|year|ετησ)/.test(s)) return 'annual';
  return undefined;
}
function mapPriority(v: unknown): Priority {
  const s = N(v);
  if (/(urgent|alert|επειγ|κρισιμ)/.test(s)) return 'urgent';
  if (/(high|υψηλ)/.test(s)) return 'high';
  if (/(low|χαμηλ)/.test(s)) return 'low';
  return 'normal';
}
function mapTaskKind(cat: unknown, type: unknown): TaskKind {
  const s = `${N(cat)} ${N(type)}`;
  if (/(appointment|ραντεβου|meeting|συναντηση)/.test(s)) return 'appointment';
  if (/(application|αιτηση)/.test(s)) return 'application';
  if (/(follow|followup|παρακολουθ)/.test(s)) return 'followup';
  if (/(review)/.test(s)) return 'review';
  if (/(renewal|ανανεωσ|anniversary|επετει)/.test(s)) return 'renewal';
  if (/(document|εγγραφ|doc)/.test(s)) return 'document';
  if (/(call|κληση|τηλεφων)/.test(s)) return 'call';
  return 'task';
}
function mapTaskStatus(t: Obj): TaskStatus {
  const s = N(t.status);
  if (t.completed === true || t.done === true || /^(completed|complete|done|ολοκληρωθηκε|closed|κλειστο)$/.test(s)) return 'done';
  if (/^(cancelled|canceled|ακυρωθηκε)$/.test(s)) return 'cancelled';
  return 'open';
}
function mapCaseStage(v: unknown): CaseStage | null {
  const s = str(v).toLowerCase();
  return (CASE_STAGES as readonly string[]).includes(s) ? (s as CaseStage) : null;
}
function mapRequirement(key: unknown): RequirementCategory | null {
  const s = str(key).toLowerCase();
  return (REQUIREMENT_CATEGORIES as readonly string[]).includes(s) ? (s as RequirementCategory) : null;
}
function mapReviewOutcome(v: unknown): ReviewOutcome {
  const s = N(v);
  if (/^(completed|positive|ολοκληρωθηκε|θετικο)$/.test(s)) return 'completed';
  if (/^(negative|αρνητικο|declined)$/.test(s)) return 'declined';
  if (/(no change|καμια αλλαγη)/.test(s)) return 'no_change';
  if (/(follow)/.test(s)) return 'needs_followup';
  return 'open';
}
const PROSPECT_STATUS_MAP: Record<string, ProspectStatus> = {
  'new': 'new', 'νεο': 'new', 'νεος': 'new', 'to call': 'new', 'για τηλεφωνημα': 'new',
  'called': 'called', 'τηλεφωνηθηκε': 'called',
  'no answer': 'no_answer', 'δεν απαντησε': 'no_answer',
  'call back': 'callback', 'callback': 'callback', 'να ξαναπαρω': 'callback',
  'interested': 'interested', 'ενδιαφερον': 'interested', 'ενδιαφερεται': 'interested',
  'appointment': 'appointment', 'appointment booked': 'appointment', 'κλειστηκε ραντεβου': 'appointment', 'ραντεβου': 'appointment',
  'not interested': 'not_interested', 'δεν ενδιαφερεται': 'not_interested',
  'wrong number': 'wrong_number', 'λαθος αριθμος': 'wrong_number',
  'converted': 'converted', 'εγινε πελατης': 'converted', 'client': 'converted',
};
function mapProspectStatus(v: unknown): ProspectStatus {
  const s = N(v);
  return PROSPECT_STATUS_MAP[s] ?? ((PROSPECT_STATUSES as readonly string[]).includes(s) ? (s as ProspectStatus) : 'new');
}
function mapTimelineType(t: unknown): TimelineType {
  const s = N(t);
  const table: [RegExp, TimelineType][] = [
    [/application|αιτηση/, 'case'], [/review/, 'review'], [/comparison|συγκρισ/, 'comparison'], [/proposal|προταση|quote/, 'proposal'],
    [/message|sms|whatsapp|μηνυμα/, 'message'], [/email/, 'email'], [/call|κληση/, 'call'], [/meeting|συναντηση|ραντεβου|appointment/, 'meeting'],
    [/policy|συμβολαιο|contract/, 'policy'], [/document|εγγραφ|file/, 'document'], [/claim|service|αποζημιωσ/, 'service'], [/note|σημειωσ/, 'note'], [/task|follow/, 'task'],
  ];
  for (const [re, v] of table) if (re.test(s)) return v;
  return 'legacy';
}
/** legacy name day: DD-MM (localDayMonth), DD/MM, or a full date → canonical MM-DD */
function mapNameDay(v: unknown): string | undefined {
  const s = str(v);
  if (!s) return undefined;
  let m = /^(\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) { const d = +m[1]!, mo = +m[2]!; if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return `${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }
  m = /^\d{4}-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[1]}-${m[2]}`;
  return undefined;
}
function toTags(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(str).filter(Boolean);
  return str(v).split(/[,;]/).map((x) => x.trim()).filter(Boolean);
}

// client-level collections extracted into LegacyRecords (kind → legacy keys)
const CLIENT_PRESERVED: [string, string[]][] = [
  ['meeting', ['meetings']], ['needs_analysis', ['needsAnalysis', 'needsAnalyses', 'needsAssessments', 'v90Suitability']],
  ['tax_profile', ['taxProfile']], ['risk_profile', ['riskProfile', 'riskQuestionnaire']], ['quote', ['quotes']], ['proposal', ['proposals']],
  ['comparison', ['policyComparisonRecords']], ['communication', ['communications', 'communicationLogs']], ['feedback', ['feedbackRecords']],
  ['outcome', ['outcomes']], ['opportunity', ['opportunityLog']], ['referral', ['referralProspects']], ['consent', ['consent']],
  ['weekly_fund', ['weeklyInvestmentUpdates']], ['pending_decision', ['pendingResponse']], ['profile_brief', ['profileBrief']],
  ['document', ['v86Documents']], ['other', ['family', 'householdProfile', 'commitments', 'plans', 'combination']],
];
const STATE_PRESERVED: Record<string, string> = {
  referrals: 'referral', opportunities: 'opportunity', consents: 'consent', quotes: 'quote', proposals: 'proposal', meetingNotes: 'meeting',
  communicationLogs: 'communication', policyComparisons: 'comparison', policyComparisonRecords: 'comparison', groupPlans: 'group_plan',
  weeklyFundUpdates: 'weekly_fund', weeklyFundDispatches: 'weekly_fund', weeklyFundSettings: 'weekly_fund', promises: 'promise', appointmentDrafts: 'other',
  syncInbox: 'sync_inbox', emailDrafts: 'communication', v87ManualProduction: 'other', marketCompanion: 'other', advisorHub: 'other', dataQuality: 'other',
  auditTrail: 'other', auditLog: 'other', productSettings: 'other', mobileTaxAnalyses: 'tax_profile', v82: 'other', v85: 'other', v86: 'other', v90: 'other', v315: 'other',
  v300Undo: 'other', v313ProspectUndo: 'other', meta: 'other',
  // keys initialised by later v9xx runtimes (observed when running the real v910 build)
  serviceCases: 'other', production: 'other', suitabilityNotes: 'needs_analysis', marketCompanionV2: 'other',
};
const STATE_HANDLED = new Set(['clients', 'tasks', 'prospectLists', 'advisor', 'settings', 'deletedClients', 'veroNotes', 'policies', 'ui', 'notified', 'registered', 'loggedIn', 'password', 'activationCode', 'license', 'lastSaved', 'updatedAt']);
const SECRET_KEYS = ['password', 'activationCode', 'license', 'registered', 'loggedIn'];
const CLIENT_MAPPED_KEYS = new Set(['contracts', 'policies', 'policy', 'plans', 'insurancePolicies', 'products', 'children', 'dependants', 'dependents', 'kids', 'claims', 'serviceCases', 'services', 'files', 'documents', 'attachments', 'history', 'timeline', 'activityLog', 'actions', 'v900Applications', 'reviewRecords']);

export function detectVersion(state: Obj, externalNotes: unknown[]): string {
  const clients = arr(state.clients).filter(isObj);
  const has = (k: string) => clients.some((c) => k in c);
  if (has('v900Applications')) return 'v900–v910 (NEXUS)';
  if (has('reviewRecords')) return 'v603–v800';
  if (Array.isArray(state.veroNotes) || externalNotes.length) return 'v600–v602';
  if (state.weeklyFundUpdates || has('weeklyInvestmentUpdates')) return 'v310–v500';
  if (state.policyComparisonRecords || state.groupPlans) return 'v230–v300';
  if (has('meetings')) return 'v224–v229';
  if (arr(state.prospectLists).length) return 'v68–v223';
  return 'v1–v67';
}

export function mapLegacyState(state: Obj, opts: MapOptions): MapResult {
  const at = opts.importedAt;
  const ds = emptyDataset();
  const warnings: ImportWarning[] = [];
  const warnCounts = new Map<string, ImportWarning>();
  const warn = (code: string, message: string, ref?: string) => {
    const w = warnCounts.get(code + message);
    if (w) { w.count = (w.count ?? 1) + 1; return; }
    const nw: ImportWarning = { code, message, ref, count: 1 };
    warnCounts.set(code + message, nw); warnings.push(nw);
  };
  const preserved: Record<string, number> = {};
  const base = (id: string, createdAt?: unknown, updatedAt?: unknown) => {
    const c = normalizeDateTime(createdAt, at);
    return { id, createdAt: c, updatedAt: normalizeDateTime(updatedAt, c), version: 1 };
  };
  const legacyCounts: Record<string, number> = {};
  const count = (k: string, n = 1) => { legacyCounts[k] = (legacyCounts[k] ?? 0) + n; };

  // ---------------------------------------------------------------- id maps
  const personByLegacyId = new Map<string, string>();
  const personByName = new Map<string, string>();
  const prospectByLegacyId = new Map<string, string>();
  const caseByLegacyId = new Map<string, string>();
  const childIds = new Map<string, string>();
  const takenIds = new Set<string>();
  const uniqueId = (kind: string, key: string) => {
    let id = stableId(kind, key), n = 1;
    while (takenIds.has(id)) id = stableId(kind, `${key}#${++n}`);
    takenIds.add(id);
    return id;
  };

  const preserve = (kind: string, payload: unknown, personId: string | undefined, key: string, title?: string, date?: unknown) => {
    if (payload === null || payload === undefined || payload === '' || (Array.isArray(payload) && !payload.length) || (isObj(payload) && !Object.keys(payload).length)) return;
    const row = { ...base(uniqueId('legacy', key), isObj(payload) ? payload.createdAt : undefined, isObj(payload) ? payload.updatedAt : undefined), kind, title: title ?? null, date: normalizeDay(date ?? (isObj(payload) ? first(payload.date, payload.createdAt) : '')) ?? undefined, payload, personId: personId ?? undefined, sourceVersion: opts.sourceLabel ?? undefined };
    ds.legacy_records.push(row as never);
    preserved[kind] = (preserved[kind] ?? 0) + 1;
  };

  // ---------------------------------------------------------------- clients
  const clients = arr(state.clients).filter(isObj);
  const deleted = arr(state.deletedClients).filter(isObj);
  count('clients', clients.length);
  count('deletedClients', deleted.length);
  const allClients: [Obj, boolean][] = [...clients.map((c) => [c, false] as [Obj, boolean]), ...deleted.map((c) => [isObj(c.client) ? c.client : c, true] as [Obj, boolean])];

  allClients.forEach(([c, isDeleted], index) => {
    const legacyId = str(first(c.id, c.clientId, c.customerId, c.personId, c.contactId, c.uuid, c._id));
    const name = str(first(c.name, c.clientName, c.fullName, c.displayName, c.customerName, [c.firstName, c.lastName].filter(Boolean).join(' ')));
    const phones: ContactPoint[] = [];
    const addPhone = (label: string, v: unknown) => { const s = str(v); if (s && !phones.some((p) => phoneMatchKey(p.value) === phoneMatchKey(s))) phones.push({ label, value: s }); };
    addPhone('mobile', first(c.phone, c.mobile, c.primaryPhone));
    addPhone('phone', c.telephone); addPhone('phone', c.tel); addPhone('phone', c.mainPhone); addPhone('phone', c.contactPhone); addPhone('phone', c.phoneNumber);
    for (const p of arr(c.phones)) addPhone('other', isObj(p) ? first(p.value, p.number) : p);
    const emails: ContactPoint[] = [];
    const addEmail = (label: string, v: unknown) => { const s = str(v); if (s && !emails.some((e) => e.value.toLowerCase() === s.toLowerCase())) emails.push({ label, value: s }); };
    addEmail('personal', first(c.email, c.primaryEmail, c.mail, c.eMail, c.contactEmail));
    for (const e of arr(c.emails)) addEmail('other', isObj(e) ? e.value : e);
    const key = legacyId || `${N(name)}|${phones[0]?.value ?? ''}|${str(c.birthDate)}|${index}`;
    if (!legacyId) warn('client.no_id', 'Client without legacy id — id derived from name/phone', name);
    const id = uniqueId('person', key);
    if (legacyId) personByLegacyId.set(legacyId, id);
    const display = name || phones[0]?.value || emails[0]?.value || `Client ${index + 1}`;
    if (!name) warn('client.no_name', 'Client without name — used phone/email as display name', display);
    if (name && !personByName.has(N(name))) personByName.set(N(name), id);

    // policies first (stage depends on them)
    const contracts = firstArr(c.contracts, c.policies, c.policy, c.plans, c.insurancePolicies, c.products).filter(isObj);
    count('contracts', contracts.length);
    let hasActive = false;
    // children
    const children = firstArr(c.children, c.dependants, c.dependents, c.kids).filter(isObj);
    count('children', children.length);
    let householdId: string | undefined;
    if (children.length || isObj(c.family) || isObj(c.householdProfile)) {
      householdId = uniqueId('household', key);
      ds.households.push({ ...base(householdId, c.createdAt, c.updatedAt), name: display } as never);
      ds.household_members.push({ ...base(uniqueId('member', `${key}|primary`), c.createdAt), householdId, personId: id, role: 'primary' } as never);
    }
    children.forEach((ch, ci) => {
      const childLegacy = str(first(ch.id, ch.childId)) || `${key}|child|${ci}`;
      const childId = uniqueId('child', childLegacy);
      childIds.set(childLegacy, childId);
      const cn = str(first(ch.name, ch.fullName)) || `Child ${ci + 1}`;
      const bd = normalizeDay(first(ch.birthDate, ch.dateOfBirth, ch.dob));
      if (str(first(ch.birthDate, ch.dateOfBirth, ch.dob)) && !bd) warn('date.invalid', 'Unreadable child birth date kept in legacy', cn);
      const sn = splitName(cn);
      ds.persons.push({ ...base(childId, c.createdAt, c.updatedAt), kind: 'dependent', stage: 'dependent', displayName: cn, firstName: sn.firstName || undefined, lastName: sn.lastName || undefined, birthDate: bd ?? undefined, notes: opt([ch.notes, ch.note].filter(Boolean).join('\n')), householdId, legacy: ch } as never);
      ds.household_members.push({ ...base(uniqueId('member', `${key}|${childLegacy}`), c.createdAt), householdId: householdId!, personId: childId, role: 'child' } as never);
    });

    contracts.forEach((p, pi) => {
      const hasText = ['productName', 'policyNumber', 'carrier', 'startDate', 'endDate', 'notes', 'contractName', 'policyName', 'product', 'planName', 'company', 'insurer'].some((k) => str(p[k]));
      const annualRaw = first(p.baseAnnual, p.annualAmount, p.annual, p.yearly, p.currentAnnual, p.currentAnnualAmount, p.premiumAnnual, p.annualPremium, p.premium, p.amount, p.sum, p.value, p.insuredAmount);
      const monthlyRaw = first(p.baseMonthly, p.monthlyAmount, p.monthly, p.currentMonthly, p.monthlyPremium, p.installment, p.monthAmount);
      const adjustments = firstArr(p.adjustments, p.policyAdjustments, p.changes, p.historyAdjustments, p.premiumChanges).filter(isObj);
      const meaningful = hasText || num(annualRaw) || num(monthlyRaw) || adjustments.length || str(p.childId) || str(p.ownerType) === 'child';
      if (!meaningful) { preserve('other', p, id, `${key}|emptycontract|${pi}`, 'empty contract'); warn('policy.empty', 'Empty contract shell preserved as record (was hidden in v910 too)'); return; }
      const legacyPid = str(first(p.id, p.policyId, p.contractId)) || `${key}|policy|${pi}`;
      const pid = uniqueId('policy', legacyPid);
      const annual = num(annualRaw) ?? (num(monthlyRaw) !== null ? round2(num(monthlyRaw)! * 12) : 0);
      const product = str(first(p.productName, p.contractName, p.policyName, p.product, p.customProductName, p.planName, p.plan, p.title, p.policyType, p.contractType, p.name, p.type, p.lineName, p.policy)) || 'Policy';
      const branchRaw = N(first(p.branch, p.category, p.class, p.line, p.businessLine, p.productClass));
      // legacy rule: an explicit branch wins (only 'general' is general); otherwise infer from the product name
      const branch = branchRaw ? (branchRaw === 'general' ? 'general' : 'life') : /medical|medica|health|accident|γενικ|general|fire|motor|travel|home|property/.test(N(product)) ? 'general' : 'life';
      const status = mapPolicyStatus(first(p.status, p.policyStatus, p.state, p.stage), (v) => warn('policy.status', `Unknown policy status "${v}" → pending`));
      if (status === 'active' || status === 'renewed') hasActive = true;
      const childRef = str(first(p.childId, p.insuredChildId));
      const insuredPersonId = childRef ? (childIds.get(childRef) ?? [...childIds.entries()].find(([k]) => k.endsWith(childRef))?.[1]) : undefined;
      const startDate = normalizeDay(first(p.startDate, p.effectiveDate, p.inceptionDate, p.fromDate, p.from));
      const renewalDate = normalizeDay(first(p.renewalDate, p.nextRenewalDate, p.anniversaryDate, p.expiryDate, p.expirationDate, p.validUntil));
      const legacyCopy: Obj = { ...p };
      if (adjustments.length) legacyCopy.adjustments = `[${adjustments.length} mapped to policy_adjustments]`;
      const docs = firstArr(p.documents, p.policyDocuments, p.attachments);
      if (docs.length) legacyCopy.documents = `[${docs.length} mapped to documents]`;
      const row: Partial<Policy> & Obj = {
        ...base(pid, first(p.createdAt, p.createdDate), p.updatedAt), personId: id, branch, productName: product, status,
        carrier: opt(first(p.carrier, p.company, p.provider, p.insurer)), policyNumber: opt(first(p.policyNumber, p.number, p.policyNo, p.contractNumber, p.policyCode)),
        startDate: startDate ?? undefined, issueDate: normalizeDay(first(p.issueDate, p.createdDate, p.applicationDate)) ?? undefined,
        renewalDate: renewalDate ?? undefined, endDate: normalizeDay(first(p.endDate, p.maturityDate, p.toDate, p.to)) ?? undefined,
        paymentFrequency: mapFrequency(first(p.paymentFrequency, p.frequency)) ?? (num(monthlyRaw) !== null && num(annualRaw) === null ? 'monthly' : undefined),
        paymentMethod: opt(first(p.paymentMethod, p.collectionMethod)), annualPremium: round2(annual), currency: opt(p.currency),
        sumAssured: num(first(p.sumAssured, p.coverAmount, p.benefitAmount)) ?? undefined, deductible: num(first(p.deductible, p.excess)) ?? undefined,
        commissionPct: num(first(p.commissionPct, p.commissionPercent)) ?? undefined, insuredPersonId, insuredName: opt(first(p.insuredName, p.insuredPerson, p.lifeAssured)),
        payerName: opt(first(p.payerName, p.premiumPayer)), beneficiaries: opt(Array.isArray(p.beneficiaries) ? p.beneficiaries.join(', ') : first(p.beneficiaries, p.beneficiary)),
        lastReviewDate: normalizeDay(first(p.lastReviewDate, p.reviewDate)) ?? undefined, nextReviewDate: normalizeDay(first(p.nextReviewDate, p.nextServiceDate)) ?? undefined,
        notes: opt([p.notes, p.description, p.memo, p.comment].map(str).filter(Boolean).join('\n')), legacy: legacyCopy,
      };
      // v1–v910 toNumber() removed every comma, so "85,50" became 8550. Flag implausible premiums for review.
      const monthlyEq = row.annualPremium! / 12;
      if (monthlyEq > 5000 || (typeof annualRaw === 'string' && /\d,\d{1,2}$/.test(annualRaw.trim())) || (typeof monthlyRaw === 'string' && /\d,\d{1,2}$/.test(monthlyRaw.trim()))) {
        row.data = { ...(row.data ?? {}), premiumSuspicious: true };
        warn('policy.premium_suspicious', 'Premium looks implausible (possible decimal-comma error in the old CRM) — flagged for review', `${display} · ${product}`);
      }
      ds.policies.push(row as never);
      adjustments.forEach((a, ai) => {
        const amount = num(a.annualAmount) ?? (num(a.monthlyAmount) !== null ? round2(num(a.monthlyAmount)! * 12) : 0);
        ds.policy_adjustments.push({ ...base(uniqueId('adjustment', str(a.id) || `${legacyPid}|adj|${ai}`), a.createdAt), policyId: pid, date: normalizeDay(a.date) ?? undefined, type: str(a.type) || 'Increase', annualAmount: amount, note: opt(a.note), legacy: a } as never);
      });
      docs.forEach((d, di) => {
        const doc = isObj(d) ? d : { title: str(d) };
        ds.documents.push({ ...base(uniqueId('document', str(doc.id) || `${legacyPid}|doc|${di}`), doc.createdAt), personId: id, policyId: pid, title: str(first(doc.title, doc.name)) || 'Document', type: opt(doc.type), reference: opt(doc.reference), url: opt(doc.url), date: normalizeDay(doc.date) ?? undefined, notes: opt(doc.notes), legacy: d } as never);
      });
    });

    const birth = normalizeDay(first(c.birthDate, c.dateOfBirth, c.dob, c.birth, c.birthday));
    if (str(first(c.birthDate, c.dateOfBirth, c.dob, c.birth, c.birthday)) && !birth) warn('date.invalid', 'Unreadable birth date kept in legacy', display);
    const sn = c.firstName || c.lastName ? { firstName: str(c.firstName), lastName: str(c.lastName) } : splitName(display);
    const lastReview = normalizeDay(c.lastReviewDate);
    const legacyCopy: Obj = {};
    for (const [k, v] of Object.entries(c)) {
      if (CLIENT_MAPPED_KEYS.has(k) && Array.isArray(v)) legacyCopy[k] = `[${v.length} mapped]`;
      else if (CLIENT_PRESERVED.some(([, keys]) => keys.includes(k))) legacyCopy[k] = '[preserved as record]';
      else legacyCopy[k] = v;
    }
    const person: Partial<Person> & Obj = {
      ...base(id, first(c.createdAt, c.createdDate), c.updatedAt), kind: 'client', stage: mapStage(first(c.status, c.clientStatus, c.stage), hasActive),
      displayName: display, firstName: sn.firstName || undefined, lastName: sn.lastName || undefined,
      phones, emails, primaryPhone: phones[0]?.value, primaryEmail: emails[0]?.value, whatsapp: opt(first(c.whatsapp, c.whatsApp, c.mobileWhatsapp)),
      gender: opt(first(c.gender, c.sex)), birthDate: birth ?? undefined, nameDay: mapNameDay(first(c.nameDay, c.nameday)),
      address: opt(first(c.address, c.homeAddress, c.location, c.regionAddress)), city: opt(c.city), region: opt(first(c.region, c.district)),
      country: opt(c.country), postalCode: opt(first(c.postalCode, c.zip)), occupation: opt(first(c.occupation, c.job, c.profession, c.work)),
      employer: opt(first(c.employer, c.company)), segment: opt(first(c.segment, c.categorySegment)), tags: toTags(c.tags),
      maritalStatus: opt(first(c.maritalStatus, c.familyStatus)), preferredChannel: opt(first(c.preferredContact, c.contactPreference)),
      source: opt(first(c.leadSource, c.source, c.leadFrom)), referredByName: opt(first(c.referredBy, c.referral, c.referrer, c.recommendedBy)),
      clientSince: normalizeDay(first(c.clientSince, c.customerSince, c.clientDate, c.since)) ?? undefined,
      notes: opt([c.notes, c.note, c.comments, c.description, c.importantInfo, c.memo].map(str).filter(Boolean).join('\n')),
      hot: c.hot === true || undefined, householdId, lastReviewDate: lastReview ?? undefined, nextReviewDate: normalizeDay(c.nextReviewDate) ?? undefined,
      smoker: typeof c.smoker === 'boolean' ? c.smoker : undefined,
      archivedAt: isDeleted ? normalizeDateTime(first(c.deletedAt, (c as Obj).removedAt), at) : undefined,
      legacy: legacyCopy,
    };
    if (person.stage !== 'client' && hasActive) person.stage = 'client';
    person.kind = person.stage === 'client' || person.stage === 'former' ? 'client' : 'prospect';
    ds.persons.push(person as never);

    // claims / service
    const claims = firstArr(c.claims, c.serviceCases, c.services).filter(isObj);
    count('claims', claims.length);
    claims.forEach((cl, i) => {
      const st = N(cl.status);
      ds.service_cases.push({ ...base(uniqueId('service', str(cl.id) || `${key}|claim|${i}`), cl.createdAt, cl.updatedAt), personId: id, title: str(first(cl.title, cl.name, cl.type)) || 'Service', status: /clos|κλειστ|ολοκλ|done|paid/.test(st) ? 'closed' : /wait|αναμον/.test(st) ? 'waiting' : /progress|εξελιξ/.test(st) ? 'in_progress' : 'open', nextAction: opt(cl.nextAction), notes: opt(cl.notes), legacy: cl } as never);
    });
    // files
    const files = firstArr(c.files, c.documents, c.attachments);
    count('files', files.length);
    files.forEach((f, i) => {
      const o = isObj(f) ? f : { title: str(f) };
      ds.documents.push({ ...base(uniqueId('document', str(o.id) || `${key}|file|${i}`), o.createdAt), personId: id, title: str(first(o.title, o.name)) || 'Document', type: opt(o.type), url: opt(o.url), reference: opt(o.reference), date: normalizeDay(o.date) ?? undefined, notes: opt(o.notes), legacy: f } as never);
    });
    // history → timeline
    const history = firstArr(c.history, c.timeline, c.activityLog, c.actions);
    count('history', history.length);
    history.forEach((h, i) => {
      const o = isObj(h) ? h : { title: 'Entry', note: str(h) };
      const title = str(first(o.title, o.type)) || 'Entry';
      ds.timeline.push({ ...base(uniqueId('timeline', str(o.id) || `${key}|hist|${i}`), first(o.createdAt, o.at, o.date)), personId: id, at: normalizeDateTime(first(o.at, o.createdAt, o.date), normalizeDateTime(c.createdAt, at)), type: mapTimelineType(first(o.type, o.title)), title, body: opt(first(o.note, o.notes, o.description)), legacy: h } as never);
    });
    // applications (v900)
    const apps = arr(c.v900Applications).filter(isObj);
    count('applications', apps.length);
    apps.forEach((a, i) => {
      const legacyAid = str(a.id) || `${key}|app|${i}`;
      const cid = uniqueId('case', legacyAid);
      caseByLegacyId.set(str(a.id) || legacyAid, cid);
      const stage = mapCaseStage(a.stage);
      if (!stage) warn('case.stage', `Unknown application stage "${str(a.stage)}" → draft`, legacyAid);
      const row: Partial<Case> & Obj = {
        ...base(cid, a.createdAt, a.updatedAt), personId: id, product: str(a.product) || 'Application', insurer: opt(a.insurer), applicationNumber: opt(a.applicationNumber),
        stage: stage ?? 'draft', priority: mapPriority(a.priority), premium: num(a.premium) ?? undefined, sumAssured: num(a.sumAssured) ?? undefined,
        targetDate: normalizeDay(a.targetDate) ?? undefined, nextAction: opt(a.nextAction), nextActionDate: normalizeDay(a.nextActionDate) ?? undefined,
        nextActionTime: normalizeTime(a.nextActionTime) ?? undefined, notes: opt(a.notes),
        submittedAt: stage && stage !== 'draft' ? normalizeDay(first(a.submittedAt, a.createdAt)) ?? undefined : undefined,
        closedAt: stage && ['issued', 'declined', 'postponed', 'withdrawn'].includes(stage) ? normalizeDateTime(a.updatedAt, at) : undefined,
        legacy: { ...a, checklist: `[${arr(a.checklist).length} mapped]`, history: `[${arr(a.history).length} mapped]` },
      };
      ds.cases.push(row as never);
      arr(a.checklist).filter(isObj).forEach((r, ri) => {
        const cat = mapRequirement(r.key);
        ds.case_requirements.push({ ...base(uniqueId('requirement', `${legacyAid}|${str(r.key) || ri}`), a.createdAt, a.updatedAt), caseId: cid, category: cat ?? 'other', label: cat ? undefined : opt(first(r.labelEn, r.labelEl, r.key)), done: r.done === true, note: opt(r.note), sort: ri } as never);
      });
      arr(a.history).filter(isObj).forEach((h, hi) => {
        ds.case_events.push({ ...base(uniqueId('case_event', str(h.id) || `${legacyAid}|h|${hi}`), h.at), caseId: cid, at: normalizeDateTime(h.at, at), stage: mapCaseStage(h.stage) ?? stage ?? 'draft', note: opt(h.note) } as never);
      });
    });
    // reviews (v603)
    const reviews = arr(c.reviewRecords).filter(isObj);
    count('reviews', reviews.length);
    reviews.forEach((r, i) => {
      const date = normalizeDay(first(r.date, r.createdAt));
      if (!date) { preserve('other', r, id, `${key}|review|${i}`, 'review without date'); return; }
      ds.reviews.push({ ...base(uniqueId('review', str(r.id) || `${key}|review|${i}`), r.createdAt), personId: id, date, nextReviewDate: normalizeDay(r.nextReviewDate) ?? undefined, outcome: mapReviewOutcome(r.outcome), notes: opt(r.notes), legacy: r } as never);
    });
    if (!reviews.length && lastReview) {
      ds.reviews.push({ ...base(uniqueId('review', `${key}|lastReview`), c.updatedAt), personId: id, date: lastReview, nextReviewDate: normalizeDay(c.nextReviewDate) ?? undefined, outcome: mapReviewOutcome(c.reviewOutcome || 'completed'), notes: opt(c.reviewNotes) } as never);
    }
    // preserved client collections
    for (const [kind, keys] of CLIENT_PRESERVED) for (const k of keys) {
      const v = c[k];
      if (Array.isArray(v)) v.forEach((item, i) => preserve(kind, item, id, `${key}|${k}|${i}`, isObj(item) ? str(first(item.title, item.type, item.name, item.comparisonTitle, item.summary)) || undefined : undefined));
      else preserve(kind, v, id, `${key}|${k}`, k);
    }
    ds.timeline.push({ ...base(uniqueId('timeline', `${key}|import`), at), personId: id, at, type: 'import', title: 'timeline.imported', body: opts.sourceLabel ?? null } as never);
  });

  // referredBy (name) → person id
  for (const p of ds.persons as unknown as Obj[]) {
    const n = str(p.referredByName);
    if (n) { const rid = personByName.get(N(n)); if (rid && rid !== p.id) p.referredByPersonId = rid; }
  }

  // ---------------------------------------------------------------- prospects
  const lists = arr(state.prospectLists ?? state.leadLists ?? state.lists).filter(isObj);
  count('prospectLists', lists.length);
  const listIdByLegacy = new Map<string, string>();
  const personByPhone = new Map<string, string>();
  for (const p of ds.persons as unknown as Obj[]) { const k = phoneMatchKey(str(p.primaryPhone)); if (k) personByPhone.set(k, str(p.id)); }
  lists.forEach((l, li) => {
    const legacyLid = str(first(l.id, l.listId)) || `list|${li}|${N(first(l.name, l.title))}`;
    const lid = uniqueId('prospect_list', legacyLid);
    listIdByLegacy.set(str(first(l.id, l.listId)) || legacyLid, lid);
    const itemsKey = ['items', 'people', 'leads', 'contacts', 'prospects', 'rows', 'records', 'entries', 'members', 'persons', 'customers', 'data'].find((k) => Array.isArray(l[k]) && (l[k] as unknown[]).length);
    const items = itemsKey ? (l[itemsKey] as unknown[]).filter(isObj) : [];
    ds.prospect_lists.push({ ...base(lid, l.createdAt, l.updatedAt), name: str(first(l.name, l.title, l.listName)) || 'Prospect list', description: opt(first(l.description, l.notes)), source: opt(first(l.defaultSource, l.source)), campaign: opt(l.campaign), archived: l.archived === true || undefined, legacy: { ...l, ...(itemsKey ? { [itemsKey]: `[${items.length} mapped]` } : {}) } } as never);
    items.forEach((raw, ii) => addProspect(raw, lid, `${legacyLid}|${ii}`));
  });
  // external arrays with listId (older generations)
  for (const k of ['prospects', 'leads', 'prospectItems']) for (const raw of arr(state[k]).filter(isObj)) {
    const lid = listIdByLegacy.get(str(raw.listId));
    if (lid) addProspect(raw, lid, `${str(raw.listId)}|ext|${str(raw.id) || N(raw.name)}`);
    else preserve('other', raw, undefined, `ext-prospect|${str(raw.id) || N(raw.name)}`, 'prospect without list');
  }
  function addProspect(raw: Obj, lid: string, fallbackKey: string) {
    count('prospects');
    const legacyPid = str(raw.id) || fallbackKey;
    const pid = uniqueId('prospect', legacyPid);
    if (str(raw.id)) prospectByLegacyId.set(str(raw.id), pid);
    const name = str(first(raw.name, raw.fullName, raw.clientName, raw['Όνομα'], raw['Name'], [raw.firstName, raw.lastName].filter(Boolean).join(' '))) || str(first(raw.phone, raw.company)) || 'Prospect';
    const converted = str(raw.convertedClientId);
    ds.prospects.push({
      ...base(pid, raw.createdAt, raw.updatedAt), listId: lid, name, phone: opt(first(raw.phone, raw.mobile, raw.telephone, raw['Τηλέφωνο'], raw['Phone'])),
      email: opt(first(raw.email, raw.mail, raw['Email'])), profession: opt(first(raw.profession, raw.occupation, raw.job, raw['Επάγγελμα'], raw['Occupation'])),
      company: opt(first(raw.company, raw.business, raw['Εταιρεία'], raw['Company'])), area: opt(first(raw.area, raw.location, raw.city, raw['Περιοχή'], raw['Area'])),
      source: opt(first(raw.source, raw.leadSource, raw.foundFrom, raw['Πηγή'], raw['Source'])), priority: mapPriority(raw.priority), status: mapProspectStatus(first(raw.status, raw['Status'], raw['Κατάσταση'])),
      notes: opt(first(raw.notes, raw.note, raw['Σημειώσεις'], raw['Notes'])), lastCallDate: normalizeDay(first(raw.lastCallDate, raw.callDate)) ?? undefined,
      nextFollowUpDate: normalizeDay(first(raw.nextFollowUpDate, raw.followUpDate)) ?? undefined, appointmentDate: normalizeDay(raw.appointmentDate) ?? undefined,
      convertedPersonId: converted ? personByLegacyId.get(converted) : undefined, legacy: raw,
    } as never);
    if (converted && !personByLegacyId.get(converted)) warn('prospect.converted_missing', 'Prospect marked converted but client not found', name);
  }

  // ---------------------------------------------------------------- tasks
  const resolvePerson = (idv: unknown, nameV: unknown): string | undefined => {
    const idS = str(idv);
    if (idS && personByLegacyId.has(idS)) return personByLegacyId.get(idS);
    const byName = str(nameV) ? personByName.get(N(nameV)) : undefined;
    return byName;
  };
  const tasks = arr(state.tasks).filter(isObj);
  count('tasks', tasks.length);
  tasks.forEach((t, i) => {
    const tid = uniqueId('task', str(t.id) || `task|${i}|${N(t.title)}`);
    const personId = resolvePerson(first(t.clientId, t.customerId, t.personId), first(t.clientName, t.customerName));
    const prospectId = str(t.prospectId) ? prospectByLegacyId.get(str(t.prospectId)) : undefined;
    if (str(first(t.clientId, t.clientName)) && !personId) warn('task.orphan', 'Task linked to a client that no longer exists — kept without link', str(t.title));
    const caseId = str(t.v900ApplicationId) ? caseByLegacyId.get(str(t.v900ApplicationId)) : undefined;
    const kind = caseId ? 'application' : mapTaskKind(t.category, first(t.type, t.kind));
    const dueDate = normalizeDay(first(t.date, t.dueDate, t.due));
    const dueTime = normalizeTime(first(t.time, t.dueTime, t.startTime));
    const status = mapTaskStatus(t);
    const loc = N(first(t.locationType, t.locationMode, t.where));
    ds.tasks.push({
      ...base(tid, t.createdAt, t.updatedAt), kind: kind === 'appointment' && !dueTime ? 'followup' : kind, title: str(first(t.title, t.summary, t.subject)) || 'Task',
      personId, prospectId, caseId, dueDate: dueDate ?? undefined, dueTime: dueTime ?? undefined, durationMin: num(first(t.duration, t.durationMin)) ?? undefined,
      locationType: /office|γραφει/.test(loc) ? 'office' : /online|zoom|teams|meet/.test(loc) ? 'online' : /client|πελατ/.test(loc) ? 'client' : /phone|τηλεφ/.test(loc) ? 'phone' : loc ? 'custom' : undefined,
      location: opt(first(t.location, t.meetingLocation, t.address)), status, priority: mapPriority(t.priority), notes: opt(first(t.notes, t.note, t.description)),
      completedAt: status === 'done' ? normalizeDateTime(first(t.completedAt, t.updatedAt), at) : undefined,
      outcome: /positive|θετικ/.test(N(t.outcome)) ? 'positive' : /negative|αρνητ/.test(N(t.outcome)) ? 'negative' : undefined,
      origin: caseId ? 'case' : 'legacy', legacy: t,
    } as never);
    // completed legacy work becomes part of "what we've done"
    if (status === 'done' && personId) {
      const tlType: TimelineType = kind === 'call' ? 'call' : kind === 'appointment' ? 'meeting' : 'task';
      ds.timeline.push({ ...base(uniqueId('timeline', `task|${tid}`), first(t.completedAt, t.updatedAt, t.createdAt)), personId, at: normalizeDateTime(first(t.completedAt, t.updatedAt, dueDate), at), type: tlType, title: str(first(t.title, t.summary)) || 'Task', refTable: 'tasks', refId: tid } as never);
    }
  });

  // ---------------------------------------------------------------- notes (v600–v602): merge sources, newest updatedAt per id wins
  const noteMap = new Map<string, Obj>();
  for (const n of [...arr(state.veroNotes), ...(opts.externalNotes ?? [])].filter(isObj)) {
    const k = str(n.id) || `${N(n.title)}|${str(n.createdAt)}`;
    const old = noteMap.get(k);
    if (!old || str(n.updatedAt ?? n.createdAt) >= str(old.updatedAt ?? old.createdAt)) noteMap.set(k, n);
  }
  count('notes', noteMap.size);
  [...noteMap.entries()].forEach(([k, n]) => {
    const kind = N(first(n.entityKind, n.kind, n.entityType));
    const personId = kind === 'prospect' ? undefined : resolvePerson(n.entityId, n.entityName);
    const prospectId = kind === 'prospect' || !personId ? prospectByLegacyId.get(str(n.entityId)) : undefined;
    if (str(n.entityId) && !personId && !prospectId) warn('note.orphan', 'Note linked to a missing person — kept unlinked', str(n.title));
    const nid = uniqueId('note', k);
    let taskId: string | undefined;
    const due = normalizeDay(n.dueDate);
    if (due) {
      taskId = uniqueId('task', `note|${k}`);
      const done = N(n.status) === 'completed';
      ds.tasks.push({ ...base(taskId, n.createdAt, n.updatedAt), kind: 'followup', title: str(n.title) || str(n.body).slice(0, 120) || 'Follow-up', personId, prospectId, dueDate: due, dueTime: normalizeTime(n.dueTime) ?? undefined, status: done ? 'done' : 'open', priority: mapPriority(n.priority), completedAt: done ? normalizeDateTime(n.completedAt, at) : undefined, outcome: n.outcome === 'positive' ? 'positive' : n.outcome === 'negative' ? 'negative' : undefined, origin: 'note', notes: opt(n.body) } as never);
    }
    ds.notes.push({ ...base(nid, n.createdAt, n.updatedAt), personId, prospectId, title: opt(n.title), body: str(first(n.body, n.text, n.note, n.title)) || '—', taskId, legacy: n } as never);
    if (personId) ds.timeline.push({ ...base(uniqueId('timeline', `note|${k}`), n.createdAt), personId, at: normalizeDateTime(n.createdAt, at), type: 'note', title: str(n.title) || 'timeline.note_added', body: opt(str(n.body).slice(0, 280)), refTable: 'notes', refId: nid } as never);
  });

  // ---------------------------------------------------------------- state-level collections
  for (const [k, v] of Object.entries(state)) {
    if (STATE_HANDLED.has(k) || k === 'prospectLists' || k === 'leadLists' || k === 'lists' || ['prospects', 'leads', 'prospectItems'].includes(k)) continue;
    const kind = STATE_PRESERVED[k] ?? 'other';
    if (!(k in STATE_PRESERVED)) warn('state.unknown_key', `Unknown top-level key "${k}" preserved as record`);
    if (Array.isArray(v)) v.forEach((item, i) => preserve(kind, item, isObj(item) ? resolvePerson(first(item.clientId, item.customerId), first(item.clientName, item.name)) : undefined, `state|${k}|${isObj(item) ? str(item.id) || i : i}`, isObj(item) ? str(first(item.title, item.name, item.comparisonTitle, item.type)) || k : k));
    else preserve(kind, v, undefined, `state|${k}`, k);
  }
  // `state.policies` in v59/v74 was a derived flattening of client contracts — preserve but never duplicate as policies
  if (Array.isArray(state.policies) && state.policies.length) { preserve('other', state.policies, undefined, 'state|policies', 'derived policies view (v59/v74)'); warn('state.policies', 'Derived state.policies list kept as a record; client contracts are the source of truth'); }

  for (const x of opts.extraRecords ?? []) preserve('other', x.payload, undefined, x.title, x.title);

  // ---------------------------------------------------------------- advisor & settings
  const settingsRows: [string, unknown][] = [];
  const adv = isObj(state.advisor) ? state.advisor : {};
  const st = isObj(state.settings) ? state.settings : {};
  if (Object.keys(adv).length) {
    const { password: _p, activationCodeUsed: _a, ...safe } = adv as Obj;
    settingsRows.push(['advisor', {
      name: str(safe.name), company: str(safe.company), phone: str(safe.phone), email: str(safe.email), website: str(safe.website),
      signature: str(safe.signature), officeAddress: str(first(safe.officeAddress, safe.address)), officeMapsUrl: str(first(safe.officeMapsUrl, safe.mapsUrl, safe.googleMapsUrl)),
      logoDataUrl: str(safe.logoDataUrl) || undefined,
    }]);
    const lang = str(first(adv.uiLanguage, adv.preferredLanguage, adv.language, st.language));
    if (lang) settingsRows.push(['locale', lang.startsWith('el') ? 'el' : lang.startsWith('en') ? 'en' : lang]);
  }
  if (isObj(st.contractCatalog) || isObj(st.contractColorMap)) settingsRows.push(['productCatalog', { catalog: st.contractCatalog ?? {}, colors: st.contractColorMap ?? {} }]);
  const targets = { month: num(st.monthGoal), year: num(st.yearGoal), life: num(st.lifeGoal), general: num(st.generalGoal), commissionPct: num(st.commissionPct) };
  if (Object.values(targets).some((x) => x)) settingsRows.push(['targets', targets]);
  if (st.theme) settingsRows.push(['theme', st.theme === 'light' ? 'light' : 'dark']);
  const restSettings = Object.fromEntries(Object.entries(st).filter(([k]) => !['contractCatalog', 'contractColorMap', 'monthGoal', 'yearGoal', 'lifeGoal', 'generalGoal', 'commissionPct', 'theme', 'language'].includes(k)));
  if (Object.keys(restSettings).length) settingsRows.push(['legacySettings', restSettings]);
  for (const [k, v] of settingsRows) ds.settings.push({ ...base(uniqueId('setting', k), at), key: k, value: v } as never);

  const excludedSecrets = SECRET_KEYS.filter((k) => state[k] !== undefined && state[k] !== '' && state[k] !== false && state[k] !== null);
  if (isObj(state.advisor) && (state.advisor as Obj).password) excludedSecrets.push('advisor.password');
  if (excludedSecrets.includes('password')) warn('security.plaintext_password', 'A plaintext password was found in the legacy data and was NOT imported. Set a new passphrase in VÉRO OS.');

  const issues = validateDataset(ds);
  if (issues.length) warn('validation', `${issues.length} validation issues (first: ${issues[0]!.table}.${issues[0]!.field} ${issues[0]!.problem})`);
  const produced = Object.fromEntries(Object.entries(ds).map(([k, v]) => [k, (v as unknown[]).length]));
  return {
    dataset: ds,
    report: { sourceVersion: detectVersion(state, opts.externalNotes ?? []), importedAt: at, legacyCounts, produced, warnings, excludedSecrets, preservedKinds: preserved, validationIssues: issues.length },
  };
}
